/**
 * Profile Service
 *
 * Handles profile fetch/update and account-deletion requests. Uses useApiFetch for
 * consistent base URL, retries, and error handling. Used by the profile store.
 */

import type { GetProfileResponse } from '~~/layers/base/server/api/profile/index.get'
import type { PatchProfileResponse } from '~~/layers/base/server/api/profile/index.patch'
import type { AddressPayload, ContactAddress } from '~~/layers/base/stores/contact.store'
import { useApiFetch } from '@/composables/useApiFetch'

export type UpdateProfileBody = {
    firstName?: string | null
    lastName?: string | null
    phoneNumber?: string | null
}

export const useProfileService = () => {
    /**
     * Fetch the signed-in user's profile.
     * @returns Promise with { data: { profile }, error, pending, refresh }
     */
    const doFetchProfile = async () => {
        const result = await useApiFetch<GetProfileResponse>('/api/profile', {
            method: 'GET',
        })
        return result
    }

    /**
     * Update the signed-in user's `first_name` / `last_name` / `phone_number`.
     * @param body – partial profile contact fields
     */
    const doUpdateProfile = async (body: UpdateProfileBody) => {
        const result = await useApiFetch<PatchProfileResponse>('/api/profile', {
            method: 'PATCH',
            body,
        })
        return result
    }

    /**
     * Request account deletion (soft-delete — doc/spec/SPEC_delete_profile.md).
     * @returns Promise with { data: { queued }, error, pending, refresh }
     */
    const doRequestAccountDeletion = async () => {
        const result = await useApiFetch<{ queued: boolean }>('/api/profile/delete-account', {
            method: 'POST',
        })
        return result
    }

    /**
     * List the signed-in user's profile-owned return addresses (not tied to a contact).
     * @returns Promise with { data: { addresses }, error, pending, refresh }
     */
    const doFetchProfileAddresses = async () => {
        const result = await useApiFetch<{ addresses: ContactAddress[] }>(
            '/api/profile/addresses',
            {
                method: 'GET',
            }
        )
        return result
    }

    /**
     * Fetch one profile-owned address by id (for the edit form).
     * @param id – address UUID
     */
    const doFetchProfileAddressById = async (id: string) => {
        const result = await useApiFetch<{ address: ContactAddress }>(
            `/api/profile/addresses/${id}`,
            { method: 'GET' }
        )
        return result
    }

    /**
     * Add a profile-owned return address. Server always sets is_default=true and clears the
     * previous default; client does not need to send is_default.
     * @param body – address payload
     */
    const doAddProfileAddress = async (body: AddressPayload) => {
        const result = await useApiFetch<{ address: ContactAddress }>('/api/profile/addresses', {
            method: 'POST',
            body,
        })
        return result
    }

    /**
     * Update one profile-owned address row (fields and/or is_default).
     * @param id – address UUID
     * @param body – partial address fields + optional is_default
     */
    const doPatchProfileAddress = async (
        id: string,
        body: Partial<AddressPayload> & { is_default?: boolean }
    ) => {
        const result = await useApiFetch<{ address: ContactAddress }>(
            `/api/profile/addresses/${id}`,
            { method: 'PATCH', body }
        )
        return result
    }

    /**
     * Delete a profile-owned address. Server refuses to delete the last remaining return
     * address (400) and promotes another row to default if the deleted one was the default.
     * @param id – address UUID
     */
    const doDeleteProfileAddress = async (id: string) => {
        const result = await useApiFetch(`/api/profile/addresses/${id}`, {
            method: 'DELETE',
        })
        return result
    }

    return {
        doFetchProfile,
        doUpdateProfile,
        doRequestAccountDeletion,
        doFetchProfileAddresses,
        doFetchProfileAddressById,
        doAddProfileAddress,
        doPatchProfileAddress,
        doDeleteProfileAddress,
    }
}
